import type { FC, MouseEvent, ReactNode } from 'react';

import type { IconName } from '@zen/Styleguide';
import useTracking from '@zen/utils/hooks/useTracking';
import { createTrackingEvent, createTrackingLabelAttribute, createTrackingParentAttribute } from '@zen/utils/tracking';

import Icon from '../../Icon';
import type { PopoverPlacement } from '../../Popover/types';
import Tooltip from '../../Tooltip';
import type { StyledButtonProps } from '../StyledButton';
import StyledButton from '../StyledButton';
import { getStyles } from './helpers';
import type { IconButtonSize } from './types';

interface Props extends Omit<StyledButtonProps, 'aria-label'> {
  className?: string;
  delayShow?: number;
  icon: IconName;
  popoverPlacement?: PopoverPlacement;
  showTooltip?: boolean;
  size?: IconButtonSize;
  title?: string;
}

export const displayName: string = 'Design System/Icon Button';

const IconButton: FC<Props> = (props) => {
  const {
    className = '',
    disabled = false,
    icon,
    onClick,
    showTooltip = true,
    size = 'large',
    title,
    popoverPlacement = 'top',
    delayShow = 500,
    type,
    ...rest
  } = props;
  const { buttonClassnames, iconClassnames } = getStyles(size);
  const { trackEvent } = useTracking();

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    const trackingEvent = createTrackingEvent(displayName, 'click', event);

    trackEvent(trackingEvent);
    onClick?.(event);
  };

  const button: ReactNode = (
    <StyledButton
      aria-label={title}
      className={`${buttonClassnames} ${className}`}
      disabled={disabled}
      onClick={handleClick}
      type={type}
      {...createTrackingParentAttribute('icon-button')}
      {...rest}
    >
      <Icon className={iconClassnames} icon={icon} {...createTrackingLabelAttribute(title || icon)} />
    </StyledButton>
  );

  if (!title || !showTooltip) {
    return button;
  }

  return (
    <Tooltip delayShow={delayShow} placement={popoverPlacement} tooltipContent={title}>
      {button}
    </Tooltip>
  );
};

export type { Props as IconButtonProps };

export default IconButton;
